import React from 'react';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import RenderTextfieldHeader from 'pages/renderTextFieldHeader';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import moment from 'moment';
import { renderSelectHeader } from 'pages/DashboardHelpers';
import Tooltip from '@mui/material/Tooltip';

const columnTypes = {
  region: { type: 'select', name: 'region', matchFields: ['region'] },
  kmat: { type: 'text', name: 'kmat', matchFields: ['kmatId'] },
  category: { type: 'select', name: 'category', matchFields: ['category'] },
  series: { type: 'select', name: 'series', matchFields: ['series'] },
  modelName: { type: 'text', name: 'modelName', matchFields: ['modelName'] },
  subCategory: { type: 'select', name: 'subCategory', matchFields: ['subCategory'] },
  author: { type: 'text', name: 'author', matchFields: ['lastModBy', 'lastModifiedDate'] },
};

const ellipsisSx = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '100%',
  display: 'block'
};

const renderEllipsisCell = (value) => (
  <Tooltip title={value || ''}>
    <Typography variant='subtitle2' sx={ellipsisSx}>
      {value}
    </Typography>
  </Tooltip>
);

const ModelColumns = ({
  filters = {},
  handleChange,
  handleEdit,
  handleDelete,
  REGIONS,
  categoryList,
  seriesList,
  subCategoryList
}) => [
  {
    headerName: 'Region',
    headerClassName: 'dashboard-data-grid-headers',
    field: 'region',
    flex: 0.4,
    sortable: false,
    renderCell: (params) => renderEllipsisCell(params.row.region),
    renderHeader: () => renderSelectHeader(columnTypes.region, filters, ' Region', handleChange, REGIONS),
  },
  {
    headerName: 'KMAT',
    headerClassName: 'dashboard-data-grid-headers',
    field: 'kmat',
    flex: 0.5,
    sortable: false,
    renderCell: (params) => renderEllipsisCell(params.row.kmatId),
    renderHeader: () => (
      <RenderTextfieldHeader
        colData={columnTypes.kmat}
        label="KMAT"
        filters={filters}
        handleChange={handleChange}
      />
    ),
  },
  {
    headerName: 'Category',
    headerClassName: 'dashboard-data-grid-headers',
    field: 'category',
    flex: 0.5,
    sortable: false,
    renderCell: (params) => renderEllipsisCell(params.row.category),
    renderHeader: () => renderSelectHeader(columnTypes.category, filters, ' Category', handleChange, categoryList),
  },
  {
    headerName: 'Series',
    headerClassName: 'dashboard-data-grid-headers',
    field: 'series',
    flex: 0.7,
    sortable: false,
    renderCell: (params) => renderEllipsisCell(params.row.series),
    renderHeader: () => renderSelectHeader(columnTypes.series, filters, ' Series', handleChange, seriesList),
  },
  {
    headerName: 'Model Name',
    headerClassName: 'dashboard-data-grid-headers',
    field: 'modelName',
    flex: 0.7,
    sortable: false,
    renderCell: (params) => renderEllipsisCell(params.row.modelName),
    renderHeader: () => (
      <RenderTextfieldHeader
        colData={columnTypes.modelName}
        label="Model Name"
        filters={filters}
        handleChange={handleChange}
      />
    ),
  },
  {
    headerName: 'Sub Category',
    headerClassName: 'dashboard-data-grid-headers',
    field: 'subCategory',
    flex: 0.5,
    sortable: false,
    renderCell: (params) => renderEllipsisCell(params.row.subCategory),
    renderHeader: () => renderSelectHeader(columnTypes.subCategory, filters, ' Sub Category', handleChange, subCategoryList),
  },
  {
    headerName: 'Author',
    field: 'author',
    headerClassName: 'dashboard-data-grid-headers',
    flex: 0.9,
    sortable: false,
    renderCell: (params) => {
      const dateValid = moment(params.row.lastModifiedDate).isValid();
      const dateStr = dateValid ? moment(params.row.lastModifiedDate).utc().format('LLLL') : '';
      const fromNowStr = dateValid ? moment(params.row.lastModifiedDate).utc().fromNow() : '';
      return (
        <Tooltip title={`${params.row.lastModBy || ''}${dateValid ? ` | ${dateStr} - ${fromNowStr}` : ''}`}>
          <div className='effective-date-data' style={{ width: '100%' }}>
            <Typography variant='subtitle2' sx={ellipsisSx}>
              {params.row.lastModBy}
            </Typography>
            {dateValid &&
              <Typography
                className='file-name effective-date-data'
                sx={{ ...ellipsisSx, fontSize: "12px" }}
              >
                {dateStr}{' - '}{fromNowStr}
              </Typography>
            }
          </div>
        </Tooltip>
      );
    },
    renderHeader: () => (
      <RenderTextfieldHeader
        colData={columnTypes.author}
        label="Author"
        filters={filters}
        handleChange={handleChange}
      />
    ),
  },
  {
    headerName: '',
    field: 'actions',
    width: 120,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      <>
        <IconButton
          color="primary"
          size="small"
          onClick={() => handleEdit(params.row)}
        >
          <DriveFileRenameOutlineIcon />
        </IconButton>
        <IconButton
          color="error"
          size="small"
          onClick={() => handleDelete(params.row)}
        >
          <DeleteIcon />
        </IconButton>
      </>
    ),
  },
];

export default ModelColumns;